import { useMemo, useState } from "react";
import { useEditorStore, type Cable } from "../app/store";
import { Icon } from "../components/Icon";
import { analyzeCableDraft, type CableDraftAnalysis } from "../domain/cable-compatibility";
import { cableEndpoints, type CableEndpoint } from "../domain/patch";
import type { InstrumentDefinition } from "../domain/instrument";
import { validateSignalPatch } from "../domain/signal-graph";
import { endpointForPortId, workspaceDefinition } from "./workspace-ports";

const cablePalette = ["#ef6b55", "#6ee672", "#60a8df", "#f2d45c", "#b784e2", "#e8e4d8"];

function endpointValue(endpoint: CableEndpoint | null) {
  if (!endpoint) return "";
  return endpoint.kind === "port" ? `port:${endpoint.portId}` : "loose";
}

function endpointFromValue(
  value: string,
  current: CableEndpoint | null,
  otherEndpoint: CableEndpoint | null,
  definition: InstrumentDefinition,
): CableEndpoint | null {
  if (!value) return null;
  if (value.startsWith("port:")) {
    return endpointForPortId(value.slice("port:".length));
  }
  if (current?.kind === "loose") return current;
  const anchor =
    current?.kind === "port" ? current : otherEndpoint?.kind === "port" ? otherEndpoint : null;
  const port = definition.ports.find(
    (candidate) => candidate.id === (anchor?.kind === "port" ? anchor.portId : ""),
  );
  return {
    kind: "loose",
    x: port?.position.x ?? definition.panel.width / 2,
    y:
      port?.position.y ??
      (port?.location === "rear"
        ? definition.panel.height - 12
        : definition.panel.mainBounds.y + definition.panel.mainBounds.height - 25),
    label: "Loose end",
  };
}

function describeEndpoint(endpoint: CableEndpoint, portNames: Map<string, string>) {
  return endpoint.kind === "loose"
    ? (endpoint.label ?? "Loose end")
    : (portNames.get(endpoint.portId) ?? endpoint.portId);
}

function EndpointSelect({
  cables,
  endpoint,
  label,
  otherEndpoint,
  onChange,
  optional = false,
}: {
  cables: Cable[];
  endpoint: CableEndpoint | null;
  label: string;
  otherEndpoint: CableEndpoint | null;
  onChange: (endpoint: CableEndpoint | null) => void;
  optional?: boolean;
}) {
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const definition = useMemo(() => workspaceDefinition(activeDocument), [activeDocument]);
  const groups = useMemo(() => {
    if (!otherEndpoint) {
      return [
        {
          label: "Front panel",
          ports: definition.ports.filter((port) => (port.location ?? "front") === "front"),
        },
        {
          label: "Rear panel",
          ports: definition.ports.filter(
            (port) =>
              port.location === "rear" &&
              !["external-equipment", "utility-equipment"].includes(port.sectionId),
          ),
        },
        {
          label: "External equipment",
          ports: definition.ports.filter((port) => port.sectionId === "external-equipment"),
        },
        {
          label: "Patch utilities",
          ports: definition.ports.filter((port) => port.sectionId === "utility-equipment"),
        },
      ];
    }

    const classified = definition.ports.map((port) => {
      const candidate = endpointForPortId(port.id);
      return {
        port,
        status: analyzeCableDraft(definition, cables, candidate, otherEndpoint).status,
      };
    });
    return [
      {
        label: "Compatible patch points",
        ports: classified.filter((item) => item.status === "valid").map((item) => item.port),
      },
      {
        label: "Experimental — review warning",
        ports: classified.filter((item) => item.status === "warning").map((item) => item.port),
      },
      {
        label: "Unavailable",
        ports: classified.filter((item) => item.status === "invalid").map((item) => item.port),
        disabled: true,
      },
    ].filter((group) => group.ports.length > 0);
  }, [cables, definition, otherEndpoint]);

  return (
    <label>
      <span>{label}</span>
      <select
        onChange={(event) =>
          onChange(endpointFromValue(event.target.value, endpoint, otherEndpoint, definition))
        }
        value={endpointValue(endpoint)}
      >
        {optional && <option value="">Choose a patch point…</option>}
        <option value="loose">Loose end</option>
        {groups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.ports.map((port) => (
              <option disabled={group.disabled} key={port.id} value={`port:${port.id}`}>
                {port.sectionId === "external-equipment"
                  ? "[External] "
                  : port.sectionId === "utility-equipment"
                    ? "[Utility] "
                    : port.location === "rear"
                      ? "[Rear] "
                      : ""}
                {port.label} — {port.direction} · {port.signalType}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

function StackablePlugOption({
  endpoint,
  label,
  onChange,
}: {
  endpoint: CableEndpoint | null;
  label: string;
  onChange: (endpoint: CableEndpoint) => void;
}) {
  const connected = endpoint?.kind === "port";
  return (
    <label className="stackable-plug-option">
      <input
        checked={connected && endpoint.plugStyle === "stackable"}
        disabled={!connected}
        onChange={(event) => {
          if (!endpoint || endpoint.kind !== "port") return;
          onChange({
            ...endpoint,
            plugStyle: event.target.checked ? "stackable" : "standard",
          });
        }}
        type="checkbox"
      />
      <span>{label} uses a stackable plug</span>
    </label>
  );
}

function BranchEndpointEditor({
  branches,
  cables,
  onChange,
  source,
}: {
  branches: CableEndpoint[];
  cables: Cable[];
  onChange: (branches: CableEndpoint[]) => void;
  source: CableEndpoint | null;
}) {
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const definition = useMemo(() => workspaceDefinition(activeDocument), [activeDocument]);
  return (
    <div className="cable-branch-editor">
      <header>
        <span>Additional branches</span>
        <button
          disabled={!source || branches.length >= 4}
          onClick={() =>
            onChange([
              ...branches,
              {
                kind: "loose",
                x: 940 + branches.length * 70,
                y: definition.panel.height + 70 + branches.length * 34,
                label: `Loose branch ${branches.length + 2}`,
              },
            ])
          }
          type="button"
        >
          <Icon name="plus" size={13} /> Add branch
        </button>
      </header>
      {branches.map((branch, index) => (
        <div className="cable-branch-row" key={index}>
          <EndpointSelect
            cables={cables}
            endpoint={branch}
            label={`Branch ${index + 2}`}
            onChange={(endpoint) => {
              if (!endpoint) return;
              onChange(
                branches.map((candidate, candidateIndex) =>
                  candidateIndex === index ? endpoint : candidate,
                ),
              );
            }}
            optional
            otherEndpoint={source}
          />
          <button
            aria-label={`Remove branch ${index + 2}`}
            onClick={() =>
              onChange(branches.filter((_, candidateIndex) => candidateIndex !== index))
            }
            type="button"
          >
            <Icon name="trash" size={13} />
          </button>
        </div>
      ))}
      {!branches.length && <p>Two-ended cable. Add a branch to create one shared assembly.</p>}
    </div>
  );
}

function RoutingFeedback({ analysis }: { analysis: CableDraftAnalysis }) {
  const title = {
    incomplete: "Choose both endpoints",
    valid: "Route ready",
    warning: "Check before saving",
    invalid: "Cannot save this route",
  }[analysis.status];

  return (
    <div
      aria-live="polite"
      className={`cable-routing-feedback is-${analysis.status}`}
      data-testid="cable-routing-feedback"
    >
      <strong>{title}</strong>
      {analysis.status === "valid" && analysis.issues.length === 0 ? (
        <p>Signal direction and type are compatible.</p>
      ) : (
        <ul>
          {analysis.issues.map((issue, index) => (
            <li className={`is-${issue.severity}`} key={`${issue.message}:${index}`}>
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ColourPicker({
  colour,
  onChange,
}: {
  colour: string;
  onChange: (colour: string) => void;
}) {
  return (
    <fieldset>
      <legend>Colour</legend>
      <div className="cable-colour-picker">
        {cablePalette.map((swatch) => (
          <button
            aria-label={`Use cable colour ${swatch}`}
            aria-pressed={colour.toLowerCase() === swatch.toLowerCase()}
            key={swatch}
            onClick={() => onChange(swatch)}
            style={{ background: swatch }}
            type="button"
          />
        ))}
        <label>
          <span>Custom</span>
          <input
            aria-label="Custom cable colour"
            onChange={(event) => onChange(event.target.value)}
            type="color"
            value={colour}
          />
        </label>
      </div>
    </fieldset>
  );
}

function CableEditor({ cable, cables }: { cable: Cable; cables: Cable[] }) {
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const definition = useMemo(() => workspaceDefinition(activeDocument), [activeDocument]);
  const deleteCable = useEditorStore((state) => state.deleteCable);
  const updateCable = useEditorStore((state) => state.updateCable);
  const [label, setLabel] = useState(cable.label ?? "");
  const [colour, setColour] = useState(cable.colour);
  const [prepared, setPrepared] = useState(Boolean(cable.prepared));
  const [endpointA, setEndpointA] = useState<CableEndpoint>(cable.endpointA);
  const [endpointB, setEndpointB] = useState<CableEndpoint>(cable.endpointB);
  const [branches, setBranches] = useState<CableEndpoint[]>(cable.branches ?? []);
  const [junction] = useState(cable.junction ?? { x: 840, y: 520 });

  const changed =
    label.trim() !== (cable.label ?? "") ||
    colour !== cable.colour ||
    prepared !== Boolean(cable.prepared) ||
    JSON.stringify(endpointA) !== JSON.stringify(cable.endpointA) ||
    JSON.stringify(endpointB) !== JSON.stringify(cable.endpointB) ||
    JSON.stringify(branches) !== JSON.stringify(cable.branches ?? []) ||
    (branches.length > 0 && JSON.stringify(junction) !== JSON.stringify(cable.junction));
  const analysis = useMemo(
    () =>
      analyzeCableDraft(
        definition,
        cables.filter((candidate) => candidate.id !== cable.id),
        endpointA,
        endpointB,
        { allowFullyLoose: prepared },
      ),
    [cable.id, cables, definition, endpointA, endpointB, prepared],
  );
  const branchAnalyses = useMemo(
    () =>
      branches.map((branch) =>
        analyzeCableDraft(
          definition,
          cables.filter((candidate) => candidate.id !== cable.id),
          endpointA,
          branch,
          { allowFullyLoose: prepared },
        ),
      ),
    [branches, cable.id, cables, definition, endpointA, prepared],
  );
  const canSave =
    changed &&
    analysis.status !== "invalid" &&
    analysis.status !== "incomplete" &&
    branchAnalyses.every(
      (branchAnalysis) =>
        branchAnalysis.status !== "invalid" && branchAnalysis.status !== "incomplete",
    );

  return (
    <form
      className="cable-manager-editor"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSave) return;
        updateCable(cable.id, {
          colour,
          endpointA,
          endpointB,
          label,
          prepared,
          branches,
          junction,
        });
      }}
    >
      <div className="cable-manager-editor-heading">
        <span style={{ background: colour }} />
        <strong>Cable {cable.number}</strong>
      </div>
      <label>
        <span>Label</span>
        <input
          onChange={(event) => setLabel(event.target.value)}
          placeholder="e.g. Filter modulation"
          value={label}
        />
      </label>
      <ColourPicker colour={colour} onChange={setColour} />
      <label className="prepared-cable-option">
        <input
          checked={prepared}
          onChange={(event) => setPrepared(event.target.checked)}
          type="checkbox"
        />
        <span>Prepared cable — both ends may remain loose</span>
      </label>
      <div className="cable-endpoint-grid">
        <EndpointSelect
          cables={cables.filter((candidate) => candidate.id !== cable.id)}
          endpoint={endpointA}
          label="End A"
          onChange={(next) => next && setEndpointA(next)}
          otherEndpoint={endpointB}
        />
        <EndpointSelect
          cables={cables.filter((candidate) => candidate.id !== cable.id)}
          endpoint={endpointB}
          label="End B"
          onChange={(next) => next && setEndpointB(next)}
          otherEndpoint={endpointA}
        />
      </div>
      <div className="cable-plug-style-grid" role="group" aria-label="Cable plug styles">
        <StackablePlugOption endpoint={endpointA} label="End A" onChange={setEndpointA} />
        <StackablePlugOption endpoint={endpointB} label="End B" onChange={setEndpointB} />
      </div>
      <BranchEndpointEditor
        branches={branches}
        cables={cables.filter((candidate) => candidate.id !== cable.id)}
        onChange={setBranches}
        source={endpointA}
      />
      {branches.length > 0 && (
        <div className="cable-junction-position">
          <span>Junction position</span>
          <output>
            {Math.round(junction.x)}, {Math.round(junction.y)}
          </output>
        </div>
      )}
      <RoutingFeedback analysis={analysis} />
      <div className="cable-manager-editor-actions">
        <button className="is-danger" onClick={() => deleteCable(cable.id)} type="button">
          <Icon name="trash" size={14} /> Delete
        </button>
        <button disabled={!canSave} type="submit">
          <Icon name="save" size={14} />{" "}
          {analysis.status === "warning" ? "Save with warning" : "Save cable"}
        </button>
      </div>
    </form>
  );
}
function NewCableEditor({
  cables,
  onCancel,
  onCreated,
}: {
  cables: Cable[];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const createCable = useEditorStore((state) => state.createCable);
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const definition = useMemo(() => workspaceDefinition(activeDocument), [activeDocument]);
  const [label, setLabel] = useState("");
  const [colour, setColour] = useState(cablePalette[cables.length % cablePalette.length]!);
  const [prepared, setPrepared] = useState(false);
  const [endpointA, setEndpointA] = useState<CableEndpoint | null>(null);
  const [endpointB, setEndpointB] = useState<CableEndpoint | null>(null);
  const [branches, setBranches] = useState<CableEndpoint[]>([]);
  const [junction] = useState({ x: 840, y: 520 });
  const analysis = useMemo(
    () =>
      analyzeCableDraft(definition, cables, endpointA, endpointB, {
        allowFullyLoose: prepared,
      }),
    [cables, definition, endpointA, endpointB, prepared],
  );
  const branchAnalyses = useMemo(
    () =>
      branches.map((branch) =>
        analyzeCableDraft(definition, cables, endpointA, branch, {
          allowFullyLoose: prepared,
        }),
      ),
    [branches, cables, definition, endpointA, prepared],
  );
  const canCreate =
    endpointA !== null &&
    endpointB !== null &&
    analysis.status !== "invalid" &&
    analysis.status !== "incomplete" &&
    branchAnalyses.every(
      (branchAnalysis) =>
        branchAnalysis.status !== "invalid" && branchAnalysis.status !== "incomplete",
    );

  return (
    <form
      className="cable-manager-editor cable-manager-new"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canCreate) return;
        createCable({
          label,
          colour,
          endpointA,
          endpointB,
          prepared,
          branches,
          junction: branches.length ? junction : undefined,
        });
        onCreated();
      }}
    >
      <div className="cable-manager-editor-heading">
        <span style={{ background: colour }} />
        <strong>New cable</strong>
      </div>
      <label>
        <span>
          Label <small>optional</small>
        </span>
        <input
          onChange={(event) => setLabel(event.target.value)}
          placeholder="e.g. Rear clock loop"
          value={label}
        />
      </label>
      <ColourPicker colour={colour} onChange={setColour} />
      <label className="prepared-cable-option">
        <input
          checked={prepared}
          onChange={(event) => {
            const next = event.target.checked;
            setPrepared(next);
            if (next) {
              setEndpointA(
                (current) =>
                  current ?? {
                    kind: "loose",
                    x: 560,
                    y: definition.panel.height + 70,
                    label: "Prepared end A",
                  },
              );
              setEndpointB(
                (current) =>
                  current ?? {
                    kind: "loose",
                    x: 790,
                    y: definition.panel.height + 70,
                    label: "Prepared end B",
                  },
              );
            }
          }}
          type="checkbox"
        />
        <span>Prepared cable — place both ends loose</span>
      </label>
      <div className="cable-endpoint-grid">
        <EndpointSelect
          cables={cables}
          endpoint={endpointA}
          label="End A"
          onChange={setEndpointA}
          optional
          otherEndpoint={endpointB}
        />
        <EndpointSelect
          cables={cables}
          endpoint={endpointB}
          label="End B"
          onChange={setEndpointB}
          optional
          otherEndpoint={endpointA}
        />
      </div>
      <div className="cable-plug-style-grid" role="group" aria-label="Cable plug styles">
        <StackablePlugOption endpoint={endpointA} label="End A" onChange={setEndpointA} />
        <StackablePlugOption endpoint={endpointB} label="End B" onChange={setEndpointB} />
      </div>
      <BranchEndpointEditor
        branches={branches}
        cables={cables}
        onChange={setBranches}
        source={endpointA}
      />
      <RoutingFeedback analysis={analysis} />
      <div className="cable-manager-editor-actions">
        <button onClick={onCancel} type="button">
          Cancel
        </button>
        <button disabled={!canCreate} type="submit">
          <Icon name="plus" size={14} />{" "}
          {analysis.status === "warning" ? "Create with warning" : "Create cable"}
        </button>
      </div>
    </form>
  );
}

export function CableManager() {
  const cables = useEditorStore((state) => state.cables);
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const definition = useMemo(() => workspaceDefinition(activeDocument), [activeDocument]);
  const select = useEditorStore((state) => state.select);
  const selectedId = useEditorStore((state) => state.selectedId);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const portNames = useMemo(
    () => new Map(definition.ports.map((port) => [port.id, port.label])),
    [definition],
  );
  const warnings = useMemo(() => validateSignalPatch(definition, cables), [cables, definition]);
  const selectedCable = cables.find((cable) => cable.id === selectedId) ?? null;

  return (
    <section aria-label="Cable manager" className={`cable-manager${open ? " is-open" : ""}`}>
      <button
        aria-expanded={open}
        className="cable-manager-toggle"
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <Icon name="cable" size={16} />
        <span>Cables</span>
        <b>{cables.length}</b>
        <Icon name="chevron" size={14} />
      </button>
      {open && (
        <div className="cable-manager-body">
          <header>
            <div>
              <span className="eyebrow">Panel tools</span>
              <h2>Patch cables</h2>
            </div>
            <div className="cable-manager-header-actions">
              <button
                className="cable-manager-add"
                onClick={() => {
                  setCreating(true);
                  select(null);
                }}
                type="button"
              >
                <Icon name="plus" size={14} />
                New cable
              </button>
              <button aria-label="Close cable manager" onClick={() => setOpen(false)} type="button">
                <Icon name="close" size={15} />
              </button>
            </div>
          </header>
          {cables.length ? (
            <ol className="cable-manager-list">
              {cables.map((cable) => {
                const cableWarnings = warnings.filter((warning) => warning.cableId === cable.id);
                return (
                  <li
                    className={!creating && selectedCable?.id === cable.id ? "is-selected" : ""}
                    key={cable.id}
                  >
                    <button
                      onClick={() => {
                        setCreating(false);
                        select(cable.id);
                      }}
                      type="button"
                    >
                      <i style={{ background: cable.colour }}>{cable.number}</i>
                      <span>
                        <strong>{cable.label || `Cable ${cable.number}`}</strong>
                        <small>
                          {describeEndpoint(cable.endpointA, portNames)} →{" "}
                          {describeEndpoint(cable.endpointB, portNames)}
                          {cableEndpoints(cable).some(
                            (endpoint) =>
                              endpoint.kind === "port" && endpoint.plugStyle === "stackable",
                          ) && " · stackable"}
                          {cable.prepared && " · prepared"}
                          {cable.branches?.length ? ` · ${cable.branches.length + 1} branches` : ""}
                        </small>
                      </span>
                      {cableWarnings.length > 0 && <em>{cableWarnings.length}</em>}
                    </button>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="cable-manager-empty">No patch cables documented.</p>
          )}
          {creating ? (
            <NewCableEditor
              cables={cables}
              onCancel={() => setCreating(false)}
              onCreated={() => setCreating(false)}
            />
          ) : selectedCable ? (
            <CableEditor
              cable={selectedCable}
              cables={cables}
              key={`${selectedCable.id}:${selectedCable.label ?? ""}:${selectedCable.colour}:${JSON.stringify(selectedCable.endpointA)}:${JSON.stringify(selectedCable.endpointB)}`}
            />
          ) : (
            cables.length > 0 && (
              <p className="cable-manager-prompt">Select a cable above to edit or remove it.</p>
            )
          )}
        </div>
      )}
    </section>
  );
}
